// The header's data-freshness badge ("Updated 4m ago"), shared by the v1 and
// v2 headers so the wording and the stale threshold cannot drift apart.
// `cacheInfo.cached_at` comes from DataContext (cacheInfoFromResponse).

/**
 * A backend timestamp as a Date. The backend writes naive
 * `datetime.now().isoformat()` strings ("2026-10-06T13:02:48.702532"), which
 * are UTC in the deployed containers; a string without a zone is therefore
 * read as UTC (the browser would read it as local time). A string with Z or
 * an offset keeps it. Null when it cannot be parsed.
 */
export function parseServerTime(value) {
    if (!value || typeof value !== 'string') return null;
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
    // Date parses at most milliseconds; trim microseconds.
    const text = value.trim().replace(/(\.\d{3})\d+/, '$1');
    const date = new Date(hasZone ? text : `${text}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The cache info DataContext keeps for an inventory response: when the data
 * was cached. The backend sends `cached_at`; `cache_expires_at` is 6 h later
 * and informational only (the old "expiry − 300 s" made every load read
 * "just now", 6 h in the future).
 */
export function cacheInfoFromResponse(response, now = Date.now()) {
    const cachedAt = parseServerTime(response?.cached_at);
    return { cached_at: (cachedAt || new Date(now)).toISOString() };
}

// From this age on the badge turns amber.
export const STALE_CACHE_MINUTES = 120;

/**
 * { label, stale, time, title } for the badge, or null when nothing is known.
 * `now` is a timestamp in ms (defaults to the current time).
 */
export function describeCacheAge(cacheInfo, now = Date.now()) {
    if (!cacheInfo) return null;
    const cachedAt = new Date(cacheInfo.cached_at);
    const ageMins = Math.floor((now - cachedAt.getTime()) / 60000);
    let label;
    if (ageMins < 1) label = 'just now';
    else if (ageMins < 60) label = `${ageMins}m ago`;
    else label = `${Math.floor(ageMins / 60)}h ago`;
    const time = cachedAt.toLocaleTimeString();
    return {
        label,
        stale: ageMins >= STALE_CACHE_MINUTES,
        time,
        title: `Data cached at ${time}`,
    };
}
